import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  Row,
  Col,
  message,
  Tag,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Divider,
  Typography,
  Drawer,
  Steps,
  Timeline,
  Badge,
} from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  SearchOutlined,
  ReloadOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { invoiceService } from '../../services';
import { formatNumber, formatDate, formatCurrency } from '../../utils/helpers';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Step } = Steps;

const ExportInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  
  const [filters, setFilters] = useState({
    search: '',
    dateRange: [],
    status: 'all',
  });

  const [statusForm] = Form.useForm();

  useEffect(() => {
    loadInvoices();
  }, [filters]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.dateRange.length === 2) {
        params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }
      if (filters.status !== 'all') params.status = filters.status;

      const response = await invoiceService.export.getAll(params);
      if (response.success) {
        setInvoices(response.data || []);
      }
    } catch (error) {
      //console.error('Error loading export invoices:', error);
      message.error('Không thể tải danh sách phiếu xuất');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (invoice) => {
    setCurrentInvoice(invoice);
    try {
      const response = await invoiceService.export.getById(invoice.invoice_id);
      if (response.success) {
        setInvoiceDetails(response.data?.details || []);
      }
    } catch (error) {
      //console.error('Error loading invoice details:', error);
      setInvoiceDetails([]);
    }
    setDetailDrawerVisible(true);
  };

  const handleUpdateStatus = (invoice) => {
    setCurrentInvoice(invoice);
    statusForm.setFieldsValue({
      status: invoice.status,
      note: '',
    });
    setStatusModalVisible(true);
  };

  const handleViewHistory = async (invoice) => {
    setCurrentInvoice(invoice);
    try {
      const response = await invoiceService.export.getStatusHistory(invoice.invoice_id);
      if (response.success) {
        setStatusHistory(response.data || []);
      }
    } catch (error) {
      //console.error('Error loading status history:', error);
      setStatusHistory([]);
    }
    setHistoryModalVisible(true);
  };

  const handleSubmitStatusUpdate = async (values) => {
    try {
      const response = await invoiceService.export.updateStatus(currentInvoice.invoice_id, values);
      if (response.success) {
        message.success('Cập nhật trạng thái thành công');
        setStatusModalVisible(false);
        loadInvoices();
      } else {
        message.error(response.message || 'Cập nhật trạng thái thất bại');
      }
    } catch (error) {
      //console.error('Error updating status:', error);
      message.error('Cập nhật trạng thái thất bại');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getStatusConfig = (status) => {
    const statusMap = {
      'pending': { color: 'default', text: 'Chờ xử lý', icon: <ClockCircleOutlined /> },
      'processing': { color: 'processing', text: 'Đang xử lý', icon: <ClockCircleOutlined /> },
      'shipping': { color: 'blue', text: 'Đang giao', icon: <ClockCircleOutlined /> },
      'delivered': { color: 'success', text: 'Đã giao', icon: <CheckCircleOutlined /> },
      'completed': { color: 'green', text: 'Hoàn thành', icon: <CheckCircleOutlined /> },
      'cancelled': { color: 'error', text: 'Đã hủy', icon: <ExclamationCircleOutlined /> },
      'returned': { color: 'warning', text: 'Trả hàng', icon: <ExclamationCircleOutlined /> },
    };
    return statusMap[status] || { color: 'default', text: status, icon: <ClockCircleOutlined /> };
  };

  const getStatusTag = (status) => {
    const config = getStatusConfig(status);
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const getStatusStep = (status) => {
    const statusOrder = ['pending', 'processing', 'shipping', 'delivered', 'completed'];
    return statusOrder.indexOf(status);
  };

  const columns = [
    {
      title: 'Mã phiếu xuất',
      dataIndex: 'invoice_id',
      key: 'invoice_id',
      render: (id) => `PX${String(id).padStart(6, '0')}`,
      sorter: (a, b) => a.invoice_id - b.invoice_id,
    },
    {
      title: 'Ngày xuất',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.invoice_date) - new Date(b.invoice_date),
    },
    {
      title: 'Khách hàng',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (name) => name || 'Khách lẻ',
    },
    {
      title: 'Địa chỉ giao hàng',
      key: 'shipping_address',
      render: (_, record) => {
        if (record.receiver_name) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{record.receiver_name}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                {record.phone}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {[record.house_number, record.street, record.ward, record.district, record.province]
                  .filter(Boolean).join(', ')}
              </div>
            </div>
          );
        }
        return 'Chưa có địa chỉ';
      },
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => formatCurrency(amount || 0),
      sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDate(date),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(record)}
            title="Xem chi tiết"
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleUpdateStatus(record)}
            title="Cập nhật trạng thái"
            disabled={record.status === 'completed'}
          />
          <Button
            icon={<HistoryOutlined />}
            size="small"
            onClick={() => handleViewHistory(record)}
            title="Lịch sử trạng thái"
          />
        </Space>
      ),
    },
  ];

  const detailColumns = [
    {
      title: 'Sản phẩm',
      key: 'product',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.product_name}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            SKU: {record.sku || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity) => formatNumber(quantity),
    },
    {
      title: 'Đơn giá',
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (price) => formatCurrency(price),
    },
    {
      title: 'Thành tiền',
      key: 'total',
      render: (_, record) => formatCurrency((record.quantity || 0) * (record.unit_price || 0)),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Title level={3}>Quản lý phiếu xuất kho</Title>
        </div>

        {/* Bộ lọc */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Input
              placeholder="Tìm kiếm theo mã phiếu, khách hàng..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8}>
            <RangePicker
              placeholder={['Từ ngày', 'Đến ngày']}
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates || [])}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="Trạng thái"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              style={{ width: '100%' }}
            >
              <Option value="all">Tất cả</Option>
              <Option value="pending">Chờ xử lý</Option>
              <Option value="processing">Đang xử lý</Option>
              <Option value="shipping">Đang giao</Option>
              <Option value="delivered">Đã giao</Option>
              <Option value="completed">Hoàn thành</Option>
              <Option value="cancelled">Đã hủy</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4} style={{ textAlign: 'right' }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadInvoices}
              loading={loading}
            />
          </Col>
        </Row>

        {/* Thống kê nhanh */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Badge count={invoices.filter(i => i.status === 'pending').length} showZero>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Chờ xử lý</div>
                </div>
              </Badge>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Badge count={invoices.filter(i => i.status === 'processing').length} showZero>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Đang xử lý</div>
                </div>
              </Badge>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Badge count={invoices.filter(i => i.status === 'shipping').length} showZero>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Đang giao</div>
                </div>
              </Badge>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Badge count={invoices.filter(i => i.status === 'completed').length} showZero>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Hoàn thành</div>
                </div>
              </Badge>
            </Card>
          </Col>
        </Row>

        {/* Bảng danh sách */}
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="invoice_id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} phiếu`,
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* Modal cập nhật trạng thái */}
      <Modal
        title="Cập nhật trạng thái phiếu xuất"
        open={statusModalVisible}
        onCancel={() => setStatusModalVisible(false)}
        footer={null}
        width={600}
      >
        {currentInvoice && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Phiếu xuất:</Text> PX{String(currentInvoice.invoice_id).padStart(6, '0')}
              <br />
              <Text strong>Khách hàng:</Text> {currentInvoice.customer_name || 'Khách lẻ'}
              <br />
              <Text strong>Trạng thái hiện tại:</Text> {getStatusTag(currentInvoice.status)}
            </div>

            <Steps
              current={getStatusStep(currentInvoice.status)}
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Step title="Chờ xử lý" />
              <Step title="Đang xử lý" />
              <Step title="Đang giao" />
              <Step title="Đã giao" />
              <Step title="Hoàn thành" />
            </Steps>

            <Form
              form={statusForm}
              layout="vertical"
              onFinish={handleSubmitStatusUpdate}
            >
              <Form.Item
                name="status"
                label="Trạng thái mới"
                rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
              >
                <Select>
                  <Option value="pending">Chờ xử lý</Option>
                  <Option value="processing">Đang xử lý</Option>
                  <Option value="shipping">Đang giao</Option>
                  <Option value="delivered">Đã giao</Option>
                  <Option value="completed">Hoàn thành</Option>
                  <Option value="cancelled">Hủy đơn</Option>
                  <Option value="returned">Trả hàng</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="note"
                label="Ghi chú"
              >
                <Input.TextArea rows={3} placeholder="Nhập ghi chú về thay đổi trạng thái" />
              </Form.Item>

              <div style={{ textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => setStatusModalVisible(false)}>
                    Hủy
                  </Button>
                  <Button type="primary" htmlType="submit">
                    Cập nhật
                  </Button>
                </Space>
              </div>
            </Form>
          </>
        )}
      </Modal>

      {/* Modal lịch sử trạng thái */}
      <Modal
        title={`Lịch sử trạng thái - PX${currentInvoice ? String(currentInvoice.invoice_id).padStart(6, '0') : ''}`}
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryModalVisible(false)}>
            Đóng
          </Button>
        ]}
        width={600}
      >
        <Timeline>
          {statusHistory.map((history, index) => {
            const config = getStatusConfig(history.status);
            return (
              <Timeline.Item
                key={index}
                dot={config.icon}
                color={config.color}
              >
                <div>
                  <div style={{ fontWeight: 'bold' }}>{config.text}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {formatDate(history.created_at)} - {history.updated_by_name}
                  </div>
                  {history.note && (
                    <div style={{ fontSize: '13px', color: '#666', marginTop: 4 }}>
                      {history.note}
                    </div>
                  )}
                </div>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </Modal>

      {/* Drawer chi tiết phiếu xuất */}
      <Drawer
        title={`Chi tiết phiếu xuất ${currentInvoice ? `PX${String(currentInvoice.invoice_id).padStart(6, '0')}` : ''}`}
        open={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
        width={800}
      >
        {currentInvoice && (
          <div>
            {/* Thông tin phiếu xuất */}
            <Card title="Thông tin chung" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text strong>Mã phiếu xuất:</Text> PX{String(currentInvoice.invoice_id).padStart(6, '0')}
                </Col>
                <Col span={12}>
                  <Text strong>Ngày xuất:</Text> {formatDate(currentInvoice.invoice_date)}
                </Col>
                <Col span={12}>
                  <Text strong>Khách hàng:</Text> {currentInvoice.customer_name || 'Khách lẻ'}
                </Col>
                <Col span={12}>
                  <Text strong>Trạng thái:</Text> {getStatusTag(currentInvoice.status)}
                </Col>
                <Col span={24}>
                  <Text strong>Địa chỉ giao hàng:</Text>
                  <div style={{ marginTop: 4 }}>
                    {currentInvoice.receiver_name && (
                      <>
                        <div><strong>{currentInvoice.receiver_name}</strong> - {currentInvoice.phone}</div>
                        <div>
                          {[currentInvoice.house_number, currentInvoice.street, currentInvoice.ward, 
                            currentInvoice.district, currentInvoice.province]
                            .filter(Boolean).join(', ')}
                        </div>
                      </>
                    )}
                  </div>
                </Col>
                {currentInvoice.note && (
                  <Col span={24}>
                    <Text strong>Ghi chú:</Text> {currentInvoice.note}
                  </Col>
                )}
              </Row>
            </Card>

            {/* Chi tiết sản phẩm */}
            <Card title="Chi tiết sản phẩm" size="small">
              <Table
                columns={detailColumns}
                dataSource={invoiceDetails}
                rowKey="detail_id"
                pagination={false}
                size="small"
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={3} style={{ textAlign: 'right' }}>
                      <div>
                        <div>Phí vận chuyển: {formatCurrency(currentInvoice.shipping_fee || 0)}</div>
                        <div>Giảm giá: {formatCurrency(currentInvoice.discount_amount || 0)}</div>
                        <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                          <Text strong>Tổng cộng:</Text>
                        </div>
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell>
                      <Text strong style={{ fontSize: '16px' }}>
                        {formatCurrency(currentInvoice.total_amount || 0)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </Card>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ExportInvoices;
